import React, { useMemo, useState } from 'react';
import HeaderLogged from './components/header_logged';
import api from './api/client';
import { toMiamiDateString } from './utils/dateUtils';
import { periodOf } from './utils/goodsOrderedReport';
import { isOff, buildConsumptionReport, sumOps, visibleOps, seedMatches, rowInDiffRange } from './utils/inventoryConsumptionReport';
import './reports.css';

const PERIODS = [{ key: 'day', label: 'Day' }, { key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }];
const TYPES = [{ key: 'all', label: 'All' }, { key: 'small-bags', label: 'Small Bags' }, { key: 'wm', label: 'Microgreens & Wildflower' }, { key: 'fertilizer', label: 'Fertilizers' }];
const COL_ID = 110;
const COL_SEED = 380;
const isDate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');
const lbs = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const signed = (n) => (n > 0.004 ? '+' : '') + lbs(Math.abs(n) < 0.005 ? 0 : n);

export default function InventoryConsumptionReportPage() {
  const today = toMiamiDateString(new Date());
  const [from,     setFrom]     = useState(today.slice(0, 8) + '01');
  const [to,       setTo]       = useState(today);
  const [period,   setPeriod]   = useState('day');
  const [showDiff, setShowDiff] = useState(false);
  const [diffFrom, setDiffFrom] = useState('');   // Diff filter, lbs; works while Show variance is on
  const [diffTo,   setDiffTo]   = useState('');
  const [type,     setType]     = useState('all');   // product type: display filter, no refetch
  const [report,   setReport]   = useState(null);   // { ops, from, to }
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [search,   setSearch]   = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const datesOk = isDate(from) && isDate(to) && from <= to;

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/api/reports/inventory-consumption', { params: { from, to } });
      setReport({ ops: r.data.operations, from, to });
      setExpanded(new Set());
      setSearch('');
    } catch (err) {
      setError('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const ops    = useMemo(() => (report ? report.ops.filter((o) => type === 'all' || o.type === type) : null), [report, type]);
  const built  = useMemo(() => (ops ? buildConsumptionReport(ops, period) : null), [ops, period]);
  const totals = useMemo(() => (ops ? sumOps(ops.map((o) => ({ ...o, periodKey: 'all' }))).total : null), [ops]);

  const q = search.trim().toLowerCase();
  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const metricLabels = showDiff ? ['Plan', 'Fact', 'Diff'] : ['Plan', 'Fact'];

  // Rows after the quick search and the Diff range. A row stays when any Diff shown in it (a period
  // cell or the Total) is inside the range; the range works only while the Diff column is shown.
  const diffActive = showDiff && (diffFrom !== '' || diffTo !== '');
  const rows = built ? built.seeds.map((s) => {
    const seedOps = visibleOps(s, q);
    if (!seedOps) return null;
    const sums = sumOps(seedOps);
    if (diffActive && !rowInDiffRange(sums, diffFrom, diffTo)) return null;
    return { s, ops: seedOps, cells: sums.cells, total: sums.total };
  }).filter(Boolean) : [];
  // Plan / Fact / Difference cells of one period; `empty` is what a period without operations shows.
  const cellTds = (c, key, empty) => {
    const off = !!c && isOff(c.plan, c.fact);
    const muted = c ? '' : ' gor-muted';
    return [
      <td key={`${key}|p`} className={`gor-td gor-num${muted}`}>{c ? lbs(c.plan) : empty}</td>,
      <td key={`${key}|f`} className={`gor-td gor-num${muted}${off ? ' gor-alert' : ''}`}>{c ? lbs(c.fact) : empty}</td>,
      showDiff && <td key={`${key}|d`} className={`gor-td gor-num${muted}${off ? ' gor-alert' : ''}`}>{c ? signed(c.fact - c.plan) : empty}</td>,
    ];
  };

  return (
    <div className="gor-canvas">
      <HeaderLogged />
      <div className="gor-page">

        <div className="gor-header">
          <h2>Inventory Consumption Report</h2>
          <p className="gor-subtitle">Seeds and fertilizers used in production against the plan, by the date the operation was closed · weights in lbs</p>
        </div>

        {error && <div className="gor-error">{error}</div>}

        {/* Filters */}
        <div className="gor-card gor-filters">
          <div className="gor-row">
            <div className="gor-field">
              <label className="gor-label">Date from *</label>
              <input type="date" className="gor-input" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="gor-field">
              <label className="gor-label">Date to *</label>
              <input type="date" className="gor-input" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="gor-field">
              <label className="gor-label">Columns by</label>
              <div className="gor-seg">
                {PERIODS.map((p) => (
                  <button key={p.key} type="button" className={`gor-seg-btn${period === p.key ? ' is-active' : ''}`} onClick={() => setPeriod(p.key)}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="gor-field">
              <label className="gor-label">Product type</label>
              <div className="gor-seg">
                {TYPES.map((t) => (
                  <button key={t.key} type="button" className={`gor-seg-btn${type === t.key ? ' is-active' : ''}`} onClick={() => setType(t.key)}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="gor-field">
              <label className="gor-check gor-inline-check">
                <input type="checkbox" checked={showDiff} onChange={(e) => setShowDiff(e.target.checked)} />
                Show variance
              </label>
            </div>
            {showDiff && (
              <div className="gor-field">
                <label className="gor-label" title="Rows that have a Diff inside the range in any period column or in Total. Minus is underuse, plus is overuse. Leave a field empty for no limit.">Diff, lbs</label>
                <div className="gor-range">
                  <input type="number" step="any" className="gor-input gor-input-num" placeholder="from" aria-label="Diff from"
                    value={diffFrom} onChange={(e) => setDiffFrom(e.target.value)} />
                  <span>to</span>
                  <input type="number" step="any" className="gor-input gor-input-num" placeholder="to" aria-label="Diff to"
                    value={diffTo} onChange={(e) => setDiffTo(e.target.value)} />
                </div>
              </div>
            )}
            <div className="gor-field gor-field-btn">
              <button className="gor-btn" onClick={generate} disabled={!datesOk || loading}>
                {loading ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>

        {!report && !loading && (
          <div className="gor-empty">Set the period, then click <strong>Generate</strong>.</div>
        )}

        {/* Results */}
        {built && (
          <div className="gor-card gor-results">
            <div className="gor-toolbar">
              <input type="search" className="gor-input gor-search" placeholder="Search by seed or material name, ID or operation ID…"
                value={search} onChange={(e) => setSearch(e.target.value)} />
              <div className="gor-summary">
                <span><strong>{ops.length}</strong> operation{ops.length === 1 ? '' : 's'}</span>
                <span>Plan <strong>{lbs(totals.plan)}</strong> lbs</span>
                <span>Fact <strong>{lbs(totals.fact)}</strong> lbs</span>
                <span>{report.from} – {report.to}</span>
                <span className="gor-undated">Red: fact differs from plan by more than 5%</span>
                {diffActive && <span><strong>{rows.length}</strong> of {built.seeds.length} rows in the Diff range</span>}
              </div>
            </div>

            {built.periods.length === 0 ? (
              <div className="gor-empty">No {type === 'all' ? '' : `${TYPES.find((t) => t.key === type).label} `}operations were closed in this period.</div>
            ) : (
              <div className="gor-table-wrap">
                <table className="gor-table">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="gor-th gor-sticky" style={{ left: 0, minWidth: COL_ID, maxWidth: COL_ID }}>ID</th>
                      <th rowSpan={2} className="gor-th gor-sticky" style={{ left: COL_ID, minWidth: COL_SEED, maxWidth: COL_SEED }}>Seed / Material</th>
                      {built.periods.map((p) => (
                        <th key={p.key} colSpan={metricLabels.length} className="gor-th gor-th-period">{p.label}</th>
                      ))}
                      <th colSpan={metricLabels.length} className="gor-th gor-th-period">Total</th>
                    </tr>
                    <tr>
                      {[...built.periods.map((p) => p.key), 'total'].map((k) => metricLabels.map((m) => (
                        <th key={`${k}|${m}`} className="gor-th gor-th-metric">{m}</th>
                      )))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td className="gor-td gor-muted" colSpan={2 + (built.periods.length + 1) * metricLabels.length}>
                          No rows match the search and the Diff range.
                        </td>
                      </tr>
                    )}
                    {rows.map(({ s, ops, cells, total }) => {
                      const viaOps = !!q && !seedMatches(s, q);   // matched by operation ID: open the row
                      const isOpen = expanded.has(s.seedsID) || viaOps;
                      return (
                        <React.Fragment key={s.seedsID}>
                          <tr className="gor-group" onClick={() => toggle(s.seedsID)}>
                            <td className="gor-td gor-sticky gor-group-name" style={{ left: 0 }}>
                              <span className="gor-chevron">{isOpen ? '▾' : '▸'}</span>{s.seedsID}
                            </td>
                            <td className="gor-td gor-sticky gor-group-name gor-name" style={{ left: COL_ID, maxWidth: COL_SEED }}>
                              {s.name}<span className="gor-count">{ops.length} operation{ops.length === 1 ? '' : 's'}</span>
                            </td>
                            {built.periods.map((p) => cellTds(cells[p.key], p.key, '—'))}
                            {cellTds(total, 'total', '—')}
                          </tr>
                          {isOpen && ops.map((o) => {
                            const label = [periodOf(o.date, 'day').label, o.typeLabel, o.product, o.workers,
                              o.otherSeeds ? `bags of ${o.otherSeeds}` : null].filter(Boolean).join(' · ');
                            const own = { plan: o.plan, fact: o.fact };
                            return (
                              <tr key={o.id} className="gor-item">
                                <td className="gor-td gor-sticky" style={{ left: 0 }}>{o.id}</td>
                                <td className="gor-td gor-sticky gor-muted gor-name" style={{ left: COL_ID, maxWidth: COL_SEED }} title={label}>{label}</td>
                                {built.periods.map((p) => cellTds(p.key === o.periodKey ? own : null, p.key, ''))}
                                {cellTds(own, 'total', '')}
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}
